//====== WR data 15/09/2026 — SmartFlow Item Importer (Fase 3)
// Motivo da alteração: camada de dados do modo "Item" do SmartFlow Importer — mesmo padrão de
// services/smartflow/importerApi.js (Parceiro), rotas novas em /api/smartflow-importer/itens/*
// (backend, Fase 1/2). Diferente de Parceiro (Fornecedor/Cliente são papéis separados, cada um com
// sua sequência), Item usa só UM bloco de config (smartflowImporter.item) — as duas pastas do
// Drive do projeto são lidas e deduplicadas JUNTAS.
//======
import api from "services/api"

const BASE = "/smartflow-importer"

//====== WR data 09/09/2026 (mesmo motivo do lado de Parceiro — cold start do Render no free tier)
async function comRetry(fn, tentativas = 4) {
  const esperas = [0, 2000, 5000, 9000]
  let ultimoErro
  for (let i = 0; i < tentativas; i++) {
    if (esperas[i]) await new Promise((r) => setTimeout(r, esperas[i]))
    try {
      return await fn()
    } catch (error) {
      ultimoErro = error
      const semResposta = !error?.response
      if (!semResposta) throw error
    }
  }
  throw ultimoErro
}

//====== Config do Importer de Item (dados do projeto — smartflowImporter.item) ======

/**
 * Lê a config de sequência do ItemCode direto do objeto do projeto já carregado, sem chamada de
 * rede — mesmo contrato de lerSequenciaDoProjeto (Parceiro), só que sem parâmetro de papel.
 * @param {Object} project doc do SmartFlowProject
 */
export function lerSequenciaItemDoProjeto(project) {
  const it = project?.smartflowImporter?.item || {}
  return {
    usaPrefixo: it.itUsaPrefixo ?? true,
    prefixo: it.itPrefixo || "IT-",
    qtdCaracteresSequencia: Number(it.itQtdCaracteresSequencia) || 5,
    proximoNumero: Number(it.itProximoNumero) || 1,
    usarSugestaoIA: it.usarSugestaoIA ?? true,
  }
}

/**
 * Avança o contador de sequência do ItemCode em `quantidade` (atômico no backend). Nunca lança
 * erro pra cima — falha silenciosa, só loga (mesmo padrão de incrementarProximoNumeroParceiro).
 */
export async function incrementarProximoNumeroItem(projectId, quantidade) {
  if (!quantidade || quantidade <= 0) return
  try {
    await comRetry(() => api.post(`${BASE}/itens/sequencia/${projectId}/incrementar`, { quantidade }))
  } catch (error) {
    console.error("[SmartFlow] Falha ao avançar o próximo número do Item:", error)
  }
}

//====== Sugestão por IA (backend, Fase 2) ======

/**
 * Sugestão em lote — nunca lança erro pra cima: falha de rede/parsing já volta tratada do backend
 * (cada item sem sugestão, com `observacao` explicando); um erro daqui é só transporte mesmo.
 * @param {string} projectId
 * @param {Array<Object>} itens itens crus extraídos do XML (com cProd/xProd/cEAN/ncm/...)
 * @param {Array<{Code:number|string, Name:string}>} gruposDisponiveis já lidos da Service Layer
 * @param {Array<{Code:string, Name:string}>} unidadesDisponiveis já lidas da Service Layer
 * @return {Promise<Array<Object>>} sugestões, uma por item de entrada (mesma ordem não garantida —
 *   casar de volta por cProd/cEAN/numeroItem/fileId, que voltam ecoados em cada sugestão)
 */
//====== WR data 15/09/2026
// Motivo da alteração: reportado ao vivo — um lote de 78 itens numa chamada só estourava o
// max_tokens da resposta da IA (cada item sugerido custa ~150-250 tokens de JSON; 78 itens não
// cabem em 4096), fazendo a IA "economizar" e devolver sugestões genéricas/repetidas em vez de
// uma por item. Fatiado em blocos de 15 (mesmo tamanho de BLOCO já usado em
// smartflowSapWrite.buscarCardCodesPorDocumentos), sequencial. O `id` que cada sugestão traz é
// local ao bloco (1..15, atribuído pelo backend) — reindexado aqui pra posição GLOBAL no array de
// entrada, senão o chamador não conseguiria casar a sugestão com o item certo.
// ======
const BLOCO_SUGESTAO_IA = 15

//====== WR data 15/09/2026
// Motivo: reportado ao vivo — sequencial (1 lote de cada vez) levava quase 1 minuto pra 78 itens
// (6 lotes x ~8-10s cada). Os lotes são INDEPENDENTES entre si (cada um é sua própria chamada à
// Anthropic, sem depender do resultado dos outros) — rodar todos em PARALELO (Promise.all) faz o
// tempo total cair pro tempo do lote mais lento, não a soma de todos. `onProgress(itensProcessados,
// totalItens, loteAtual, totalLotes)` é chamado conforme cada lote VAI terminando (ordem de
// chegada, não de índice) — itensProcessados soma o tamanho de cada lote concluído (WR
// 16/09/2026, pedido do usuário: "X de Y itens processados", mais concreto que "lote X de Y").
// ======
export async function sugerirItensComIA(projectId, itens, gruposDisponiveis, unidadesDisponiveis, onProgress) {
  if (!itens?.length) return []

  const blocos = []
  for (let i = 0; i < itens.length; i += BLOCO_SUGESTAO_IA) {
    blocos.push({ inicioGlobal: i, parte: itens.slice(i, i + BLOCO_SUGESTAO_IA) })
  }

  let concluidos = 0
  let itensProcessados = 0
  const resultadosPorBloco = await Promise.all(
    blocos.map(async ({ inicioGlobal, parte }) => {
      //====== WR data 15/09/2026
      // Motivo: reportado ao vivo — "preparando" ficava minutos sem terminar nem dar erro. Cada
      // lote faz uma chamada real à Anthropic (podendo demorar); `api` não tem timeout nenhum,
      // então um lote travado prendia a tela pra sempre. Rodando os lotes em paralelo (acima), o
      // timeout de cada um não se acumula mais no tempo total — por isso subiu de 45s pra 60s
      // (visto ao vivo: um lote sequencial estourou 45s de verdade, sem estar travado, só lento).
      // Como um timeout aqui não tem `response`, o comRetry trata como erro de rede e tenta de
      // novo antes de desistir.
      // ======
      const { data } = await comRetry(() =>
        api.post(
          `${BASE}/itens/${projectId}/sugerir`,
          {
            itens: parte,
            gruposDisponiveis: gruposDisponiveis || [],
            unidadesDisponiveis: unidadesDisponiveis || [],
          },
          { timeout: 60000 }
        )
      )
      concluidos += 1
      itensProcessados += parte.length
      if (onProgress) onProgress(itensProcessados, itens.length, concluidos, blocos.length)
      return (data?.sugestoes || []).map((s) => ({ ...s, id: inicioGlobal + s.id }))
    })
  )
  const resultado = resultadosPorBloco.flat()
  return resultado
}

//====== Itens — staging ======

/**
 * Upsert em lote por {project, itemCode}. `rows` são as linhas no formato SAP (saída de
 * montarItemSap) — o backend mapeia pros campos camelCase do model.
 */
export async function salvarItensStaging(projectId, rows, preparadoPor) {
  if (!rows?.length) return []
  const { data } = await comRetry(() => api.post(`${BASE}/itens/${projectId}/staging`, { rows, preparadoPor }))
  return data
}

export const ITENS_STAGING_PAGE_SIZE = 20

/**
 * Lista/busca/pagina a staging de itens de um projeto (tela "Itens Importados").
 * @param {{ projectId: string, termo?: string, page?: number }} params
 */
export async function listarItensStaging({ projectId, termo, page = 1 } = {}) {
  const { data } = await api.get(`${BASE}/itens/${projectId}/staging`, {
    params: { termo: termo || undefined, page },
  })
  return data
}

/**
 * Exclusão FÍSICA de um item da staging — quem/quando fica no log. NÃO exclui do SAP (isso é
 * excluirItemDoSap em itemSapWrite.js, chamado à parte pela tela).
 */
export async function excluirItemStaging(item, excluidoPor) {
  await comRetry(() => api.delete(`${BASE}/itens/staging/${item._id}`, { data: { excluidoPor } }))
}

/**
 * Mapa { [cEAN]: itemCode } de QUALQUER staging do projeto — aviso na aba "Itens identificados".
 */
export async function buscarItensJaImportadosPorEan(projectId, eans) {
  const { data } = await api.post(`${BASE}/itens/${projectId}/ja-importados`, { eans: eans || [] })
  return data || {}
}

/**
 * Mapa { [cEAN]: itemCode } só dos já gravados no SAP (status gravado_sap).
 */
export async function buscarItensGravadosNoSapPorEan(projectId, eans) {
  const { data } = await api.post(`${BASE}/itens/${projectId}/gravados-sap`, { eans: eans || [] })
  return data || {}
}

/**
 * Registra no backend que um item foi gravado de verdade no SAP (status gravado_sap + log).
 * @param {string} stagingId _id do doc de staging
 */
export async function marcarItemGravadoNoSap(stagingId, { gravadoPor, itemCode } = {}) {
  const { data } = await comRetry(() => api.patch(`${BASE}/itens/staging/${stagingId}/gravado`, { gravadoPor, itemCode }))
  return data
}
